@if (subscription(); as sub) {
  <div class="flex flex-col gap-4">
    <div class="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
      <div class="flex items-center gap-2 px-4 py-2.5 bg-slate-100 border-b border-slate-200">
        <span class="flex h-7 w-7 items-center justify-center rounded-lg bg-violet-100 text-violet-600">
          <i class="pi pi-info-circle text-sm"></i>
        </span>
        <span class="text-xs font-semibold text-slate-700 uppercase tracking-wide">Información general</span>
      </div>
      <div class="p-4">
        <div class="flex items-center gap-2 mb-4 pb-3 border-b border-slate-100">
          <div
            class="shrink-0 flex items-center justify-center [&>i]:text-xs"
            [class]="getStatusIconClasses(sub.status, true)">
            <i [class]="getStatusIcon(sub.status)"></i>
          </div>
          <div class="min-w-0 flex-1">
            <div class="flex flex-wrap gap-1">
              <span class="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{{ getStatusLabel(sub.status) }}</span>
              @if (sub.frequency) {
                <span class="text-[10px] px-1.5 py-0.5 rounded bg-violet-100 text-violet-700">{{ getFrequencyLabel(sub.frequency) }}</span>
              }
              @if (sub.collectionMethod) {
                <span class="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{{ getCollectionMethodLabel(sub.collectionMethod) }}</span>
              }
            </div>
          </div>
          <span class="text-sm font-bold text-slate-900 shrink-0 tabular-nums">
            {{ (sub.total ?? 0) | currency:(sub.currency || 'MXN'):'symbol':'1.2-2' }}
          </span>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
          <div>
            <p class="text-xs font-medium text-slate-500">Folio</p>
            <p class="text-sm text-slate-700">{{ sub.number || ('#' + sub.id) }}</p>
          </div>
          @if (sub.plan?.name) {
            <div>
              <p class="text-xs font-medium text-slate-500">Plan</p>
              <p class="text-sm text-slate-700">{{ sub.plan.name }}</p>
            </div>
          }
          <div>
            <p class="text-xs font-medium text-slate-500">Subtotal</p>
            <p class="text-sm text-slate-700">{{ (sub.subtotal ?? 0) | currency:(sub.currency || 'MXN'):'symbol':'1.2-2' }}</p>
          </div>
          <div>
            <p class="text-xs font-medium text-slate-500">Impuestos</p>
            <p class="text-sm text-slate-700">{{ (sub.tax ?? 0) | currency:(sub.currency || 'MXN'):'symbol':'1.2-2' }}</p>
          </div>
          @if ((sub.discount ?? 0) > 0) {
            <div>
              <p class="text-xs font-medium text-slate-500">Descuento</p>
              <p class="text-sm text-slate-700">{{ sub.discount | currency:(sub.currency || 'MXN'):'symbol':'1.2-2' }}</p>
            </div>
          }
          <div>
            <p class="text-xs font-medium text-slate-500">Fecha de creación</p>
            <p class="text-sm text-slate-700">{{ sub.createdAt ? (sub.createdAt | date:'dd/MM/yyyy') : '—' }}</p>
          </div>
          @if (sub.startDate) {
            <div>
              <p class="text-xs font-medium text-slate-500">Fecha de inicio</p>
              <p class="text-sm text-slate-700">{{ sub.startDate | date:'dd/MM/yyyy' }}</p>
            </div>
          }
          @if (sub.nextBillingDate) {
            <div>
              <p class="text-xs font-medium text-slate-500">Próximo cobro</p>
              <p class="text-sm text-slate-700">{{ formatApiDateOnly(sub.nextBillingDate) || '—' }}</p>
            </div>
          }
          @if (sub.endDate) {
            <div>
              <p class="text-xs font-medium text-slate-500">Fecha de fin</p>
              <p class="text-sm text-slate-700">{{ sub.endDate | date:'dd/MM/yyyy' }}</p>
            </div>
          }
        </div>
        <div class="mt-4 pt-3 border-t border-slate-100 w-full">
          <p class="text-xs font-medium text-slate-500">Notas</p>
          <p class="text-sm text-slate-700 whitespace-pre-wrap break-words">{{ sub.notes?.trim() || '—' }}</p>
        </div>
      </div>
    </div>

    <div class="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
      <div class="flex items-center gap-2 px-4 py-2.5 bg-slate-100 border-b border-slate-200">
        <span class="flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-100 text-emerald-600">
          <i class="pi pi-user text-sm"></i>
        </span>
        <span class="text-xs font-semibold text-slate-700 uppercase tracking-wide">Cliente</span>
      </div>
      <div class="p-4">
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
          <div>
            <p class="text-xs font-medium text-slate-500">Nombre comercial</p>
            <p class="text-sm font-medium text-slate-900">{{ sub.customer?.comercialName || sub.customerName || '—' }}</p>
          </div>
          <div>
            <p class="text-xs font-medium text-slate-500">Razón social</p>
            <p class="text-sm text-slate-700">{{ sub.customer?.legalName || '—' }}</p>
          </div>
          @if (sub.customer?.taxId) {
            <div>
              <p class="text-xs font-medium text-slate-500">RFC</p>
              <p class="text-sm text-slate-700 font-mono">{{ sub.customer.taxId }}</p>
            </div>
          }
          @if (sub.customer?.email || sub.customerEmail) {
            <div>
              <p class="text-xs font-medium text-slate-500">Email</p>
              <a
                [href]="'mailto:' + (sub.customer?.email || sub.customerEmail)"
                class="text-sm text-sky-600 hover:text-sky-800">
                {{ sub.customer?.email || sub.customerEmail }}
              </a>
            </div>
          }
        </div>
        @if (sub.customer?.id) {
          <button
            type="button"
            class="inline-flex items-center gap-1.5 mt-3 text-sm text-sky-600 hover:text-sky-800"
            (click)="openCustomerDetails()">
            <i class="pi pi-eye text-[10px] shrink-0"></i>
            <span>Ver cliente</span>
          </button>
        }
      </div>
    </div>

    @if (sub.plan) {
      <div class="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
        <div class="flex items-center gap-2 px-4 py-2.5 bg-slate-100 border-b border-slate-200">
          <span class="flex h-7 w-7 items-center justify-center rounded-lg bg-violet-100 text-violet-600">
            <i class="pi pi-box text-sm"></i>
          </span>
          <span class="text-xs font-semibold text-slate-700 uppercase tracking-wide">Plan asociado</span>
        </div>
        <div class="p-4">
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
            <div>
              <p class="text-xs font-medium text-slate-500">Nombre</p>
              <p class="text-sm font-medium text-slate-900">{{ sub.plan.name || '—' }}</p>
            </div>
            @if (sub.plan.description) {
              <div class="sm:col-span-2">
                <p class="text-xs font-medium text-slate-500">Descripción</p>
                <p class="text-sm text-slate-700">{{ sub.plan.description }}</p>
              </div>
            }
          </div>
          @if (sub.plan.id) {
            <a
              [routerLink]="['/plans', sub.plan.id]"
              class="inline-flex items-center gap-1.5 mt-3 text-sm text-violet-600 hover:text-violet-800">
              <i class="pi pi-external-link text-[10px] shrink-0"></i>
              <span>Ver plan</span>
            </a>
          }
        </div>
      </div>
    }

    @if (sub.lines && sub.lines.length > 0) {
      <div class="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
        <div class="flex items-center gap-2 px-4 py-2.5 bg-slate-100 border-b border-slate-200">
          <span class="flex h-7 w-7 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
            <i class="pi pi-list text-sm"></i>
          </span>
          <span class="text-xs font-semibold text-slate-700 uppercase tracking-wide">Líneas ({{ sub.lines.length }})</span>
        </div>
        <div class="divide-y divide-slate-100">
          @for (line of sub.lines; track line.id ?? $index) {
            <div class="flex items-center gap-2 px-4 py-2">
              <div class="shrink-0 flex h-7 w-7 items-center justify-center rounded-full bg-sky-100 text-sky-600 [&>i]:text-xs">
                <i class="pi pi-box"></i>
              </div>
              <div class="min-w-0 flex-1">
                <p class="text-xs font-medium text-slate-900 truncate">{{ line.description || 'Sin descripción' }}</p>
                @if (line.quantity != null && line.unitPrice != null) {
                  <p class="text-[11px] text-slate-500 mt-0.5">
                    {{ line.quantity }} × {{ line.unitPrice | currency:(sub.currency || 'MXN') }}
                  </p>
                }
              </div>
              <span class="text-xs font-bold text-slate-900 shrink-0 tabular-nums">
                {{ (line.totalAmount ?? line.quantity! * (line.unitPrice ?? 0)) | currency:(sub.currency || 'MXN'):'symbol':'1.2-2' }}
              </span>
            </div>
          }
        </div>
      </div>
    }

    <div class="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
      <div class="flex items-center gap-2 px-4 py-2.5 bg-slate-100 border-b border-slate-200">
        <span class="flex h-7 w-7 items-center justify-center rounded-lg bg-violet-100 text-violet-600">
          <i class="pi pi-shopping-cart text-sm"></i>
        </span>
        <span class="text-xs font-semibold text-slate-700 uppercase tracking-wide">Ventas asociadas</span>
      </div>
      @if (salesLoading()) {
        <div class="flex flex-col items-center justify-center py-8 gap-3">
          <i class="pi pi-spin pi-spinner text-2xl text-sky-500"></i>
          <p class="text-sm text-slate-500">Cargando ventas...</p>
        </div>
      } @else if (sales().length === 0) {
        <p class="text-sm text-slate-500 px-4 py-3">No hay ventas asociadas a esta suscripción.</p>
      } @else {
        <div class="divide-y divide-slate-100">
          @for (sale of sales(); track sale.id ?? $index) {
            <div class="flex items-center gap-2 px-4 py-2">
              <div class="shrink-0 flex items-center justify-center [&>i]:text-xs"
                [class]="getSaleStatusIconClasses(sale.status, true)">
                <i [class]="getSaleStatusIcon(sale.status)"></i>
              </div>
              <div class="min-w-0 flex-1">
                <div class="flex items-center gap-2 min-w-0">
                  <span class="text-xs font-semibold text-slate-900 truncate">{{ sale.number || ('#' + sale.id) }}</span>
                  <span class="text-[11px] text-slate-500 shrink-0">
                    {{ sale.createdAt ? (sale.createdAt | date:'dd/MM/yyyy') : '—' }}
                  </span>
                </div>
                <div class="flex flex-wrap gap-1 mt-0.5">
                  <span class="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{{ getSaleStatusLabel(sale.status) }}</span>
                </div>
              </div>
              <span class="text-xs font-bold text-slate-900 shrink-0 tabular-nums">
                {{ (sale.total ?? 0) | currency:(sale.currency || 'MXN'):'symbol':'1.2-2' }}
              </span>
              @if (sale.id != null) {
                <button
                  type="button"
                  class="shrink-0 flex h-7 w-7 items-center justify-center rounded-md text-sky-600 hover:bg-sky-50 hover:text-sky-800"
                  title="Ver venta"
                  (click)="openSaleDetails(sale)">
                  <i class="pi pi-eye text-xs"></i>
                </button>
              }
            </div>
          }
        </div>

        @if (salesHasMore()) {
          <div class="flex justify-center py-2 border-t border-slate-100">
            <p-button
              label="Cargar más"
              icon="pi pi-plus"
              severity="secondary"
              [outlined]="true"
              size="small"
              [loading]="salesLoadingMore()"
              (onClick)="loadMoreSales()" />
          </div>
        }
      }
    </div>
  </div>
}
